import React, { useState, useEffect } from 'react'
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  TextField,
  Button,
  Tabs,
  Tab,
  Alert,
  CircularProgress,
  Snackbar,
  Paper,
  Divider,
  Chip,
  Avatar,
  Stack,
} from '@mui/material'
import {
  Business as BusinessIcon,
  ContactMail as ContactIcon,
  LocationOn as LocationIcon,
  ReceiptLong as TaxIcon,
  Palette as BrandingIcon,
  AccountBalance as BankIcon,
  Description as DocumentNotesIcon,
  Save as SaveIcon,
  Refresh as RefreshIcon,
  CheckCircle as CheckCircleIcon,
} from '@mui/icons-material'
import { companyProfileService, CompanyProfile } from '../../services/companyProfileService'

export const CompanyProfilePage: React.FC = () => {
  const [activeTab, setActiveTab] = useState(0)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [profile, setProfile] = useState<Partial<CompanyProfile>>({})
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success',
  })

  const loadProfile = async () => {
    try {
      setLoading(true)
      const data = await companyProfileService.getProfile()
      setProfile(data)
    } catch (err: any) {
      setSnackbar({
        open: true,
        message: err?.response?.data?.detail || 'Failed to load company profile',
        severity: 'error',
      })
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadProfile()
  }, [])

  const handleChange = (field: keyof CompanyProfile, value: any) => {
    setProfile((prev) => ({
      ...prev,
      [field]: value,
    }))
  }

  const handleSave = async () => {
    try {
      setSaving(true)
      const updated = await companyProfileService.updateProfile(profile)
      setProfile(updated)
      setSnackbar({
        open: true,
        message: 'Company master data & branding saved successfully!',
        severity: 'success',
      })
    } catch (err: any) {
      setSnackbar({
        open: true,
        message: err?.response?.data?.detail || 'Failed to save company profile',
        severity: 'error',
      })
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return (
      <Box sx={{ p: 4, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
      </Box>
    )
  }

  return (
    <Box sx={{ p: 3, maxWidth: 1200, margin: '0 auto' }}>
      {/* Header Banner */}
      <Paper
        elevation={0}
        sx={{
          p: 3,
          mb: 3,
          borderRadius: 2,
          background: 'linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)',
          color: 'white',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 2,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Avatar
            sx={{
              width: 56,
              height: 56,
              bgcolor: 'rgba(255, 255, 255, 0.2)',
              color: 'white',
            }}
          >
            <BusinessIcon fontSize="large" />
          </Avatar>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 700, letterSpacing: '-0.02em' }}>
              Company Profile & Brand Master
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.9, mt: 0.5 }}>
              Centralized master data for company information, contact details, branding colors, tax registration, and banking.
            </Typography>
          </Box>
        </Box>
        <Stack direction="row" spacing={1.5}>
          <Button
            variant="outlined"
            onClick={loadProfile}
            disabled={saving}
            startIcon={<RefreshIcon />}
            sx={{
              color: 'white',
              borderColor: 'rgba(255,255,255,0.4)',
              '&:hover': { borderColor: 'white', bgcolor: 'rgba(255,255,255,0.1)' },
            }}
          >
            Reset
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={saving}
            startIcon={saving ? <CircularProgress size={18} color="inherit" /> : <SaveIcon />}
            sx={{
              bgcolor: '#10b981',
              color: 'white',
              fontWeight: 600,
              '&:hover': { bgcolor: '#059669' },
            }}
          >
            {saving ? 'Saving...' : 'Save Master Data'}
          </Button>
        </Stack>
      </Paper>

      {/* Tabs Layout */}
      <Card elevation={1} sx={{ borderRadius: 2 }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}>
          <Tabs
            value={activeTab}
            onChange={(_, val) => setActiveTab(val)}
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              '& .MuiTab-root': {
                fontWeight: 600,
                textTransform: 'none',
                minHeight: 56,
              },
            }}
          >
            <Tab icon={<BusinessIcon />} iconPosition="start" label="General Information" />
            <Tab icon={<ContactIcon />} iconPosition="start" label="Contact & WhatsApp" />
            <Tab icon={<LocationIcon />} iconPosition="start" label="Registered Address" />
            <Tab icon={<TaxIcon />} iconPosition="start" label="Tax & Legal (GST/PAN)" />
            <Tab icon={<BrandingIcon />} iconPosition="start" label="Branding & Colors" />
            <Tab icon={<BankIcon />} iconPosition="start" label="Banking & UPI" />
            <Tab icon={<DocumentNotesIcon />} iconPosition="start" label="Document Notes & Terms" />
          </Tabs>
        </Box>

        <CardContent sx={{ p: 3 }}>
          {/* TAB 0: General Info */}
          {activeTab === 0 && (
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                  Basic Business Details
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Display name and legal corporate entity name used across invoices, estimates, and customer notifications.
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Display Brand / Trade Name *"
                  value={profile.company_name || ''}
                  onChange={(e) => handleChange('company_name', e.target.value)}
                  placeholder="e.g. Dhanshika Exports"
                  helperText="Primary brand name displayed on headers, portal, and customer messages"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Full Legal Entity Name"
                  value={profile.legal_name || ''}
                  onChange={(e) => handleChange('legal_name', e.target.value)}
                  placeholder="e.g. Dhanshika Exports Imports Pvt. Ltd."
                  helperText="Registered business entity name printed on official tax invoices"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Tagline / Slogan"
                  value={profile.tagline || ''}
                  onChange={(e) => handleChange('tagline', e.target.value)}
                  placeholder="e.g. Premier B2B Export & Trading Partner"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Official Website URL"
                  value={profile.website || ''}
                  onChange={(e) => handleChange('website', e.target.value)}
                  placeholder="e.g. https://dhanshika.com"
                />
              </Grid>
            </Grid>
          )}

          {/* TAB 1: Contact & WhatsApp */}
          {activeTab === 1 && (
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                  Communication & Support Numbers
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Used dynamically in transactional emails, automated WhatsApp alerts, and payment receipts.
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Primary Contact / WhatsApp Number *"
                  value={profile.whatsapp_number || ''}
                  onChange={(e) => handleChange('whatsapp_number', e.target.value)}
                  placeholder="e.g. +91 9876543210"
                  helperText="Business phone and WhatsApp number sent to customers for order updates"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Alternate Office Phone / Landline"
                  value={profile.phone || ''}
                  onChange={(e) => handleChange('phone', e.target.value)}
                  placeholder="e.g. +91 1234567890"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="General Business Email *"
                  value={profile.email || ''}
                  onChange={(e) => handleChange('email', e.target.value)}
                  placeholder="e.g. info@dhanshika.com"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Customer Support Email"
                  value={profile.support_email || ''}
                  onChange={(e) => handleChange('support_email', e.target.value)}
                  placeholder="e.g. support@dhanshika.com"
                />
              </Grid>
            </Grid>
          )}

          {/* TAB 2: Address */}
          {activeTab === 2 && (
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                  Registered Office & Dispatch Address
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Official business address printed on Tax Invoices, Delivery Challans, and Shipping Labels.
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Address Line 1 *"
                  value={profile.address_line1 || ''}
                  onChange={(e) => handleChange('address_line1', e.target.value)}
                  placeholder="e.g. Ground Floor, Plot No. 297, Khata No. 1"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Address Line 2"
                  value={profile.address_line2 || ''}
                  onChange={(e) => handleChange('address_line2', e.target.value)}
                  placeholder="e.g. JS College Road, Nimiya, Opposite JS College"
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  label="City *"
                  value={profile.city || ''}
                  onChange={(e) => handleChange('city', e.target.value)}
                  placeholder="e.g. Daltonganj"
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  label="State *"
                  value={profile.state || ''}
                  onChange={(e) => handleChange('state', e.target.value)}
                  placeholder="e.g. Jharkhand"
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  label="Pincode *"
                  value={profile.pincode || ''}
                  onChange={(e) => handleChange('pincode', e.target.value)}
                  placeholder="e.g. 822101"
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  label="Country *"
                  value={profile.country || ''}
                  onChange={(e) => handleChange('country', e.target.value)}
                  placeholder="e.g. India"
                />
              </Grid>
            </Grid>
          )}

          {/* TAB 3: Tax & Legal */}
          {activeTab === 3 && (
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                  GSTIN & Statutory Registrations
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  GSTIN, PAN, and Corporate Identification Number for statutory compliance.
                </Typography>
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="GSTIN *"
                  value={profile.gstin || ''}
                  onChange={(e) => handleChange('gstin', e.target.value.toUpperCase())}
                  placeholder="e.g. 20AABCD1234E1Z5"
                  helperText="15-character Goods & Services Tax Identification Number"
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Permanent Account Number (PAN)"
                  value={profile.pan || ''}
                  onChange={(e) => handleChange('pan', e.target.value.toUpperCase())}
                  placeholder="e.g. AABCD1234E"
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Corporate Identity Number (CIN)"
                  value={profile.cin || ''}
                  onChange={(e) => handleChange('cin', e.target.value.toUpperCase())}
                  placeholder="e.g. U51909JH2022PTC018234"
                />
              </Grid>
            </Grid>
          )}

          {/* TAB 4: Branding & Colors */}
          {activeTab === 4 && (
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                  Brand Identity & Theme Palette
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Configure logo assets and primary brand colors used across the portal, PDF headers, and notification styling.
                </Typography>
              </Grid>

              {/* Logo URL */}
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Primary Logo URL"
                  value={profile.logo_url || ''}
                  onChange={(e) => handleChange('logo_url', e.target.value)}
                  placeholder="e.g. /assets/images/logo.png"
                  helperText="URL to brand logo displayed on portal navigation and invoice headers"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Dark / Reverse Logo URL"
                  value={profile.dark_logo_url || ''}
                  onChange={(e) => handleChange('dark_logo_url', e.target.value)}
                  placeholder="e.g. /assets/images/logo-dark.png"
                  helperText="Optional logo for dark backgrounds"
                />
              </Grid>

              {/* Colors */}
              <Grid item xs={12} md={4}>
                <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                    Primary Brand Color
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <input
                      type="color"
                      value={profile.primary_color || '#1e3a8a'}
                      onChange={(e) => handleChange('primary_color', e.target.value)}
                      style={{ width: 44, height: 44, border: 'none', cursor: 'pointer', borderRadius: 4 }}
                    />
                    <TextField
                      size="small"
                      value={profile.primary_color || '#1e3a8a'}
                      onChange={(e) => handleChange('primary_color', e.target.value)}
                      sx={{ flexGrow: 1 }}
                    />
                  </Box>
                  <Box sx={{ mt: 1.5, p: 1, bgcolor: profile.primary_color || '#1e3a8a', color: 'white', borderRadius: 1, textAlign: 'center', fontSize: '0.8rem', fontWeight: 600 }}>
                    Primary Preview
                  </Box>
                </Paper>
              </Grid>

              <Grid item xs={12} md={4}>
                <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                    Secondary Accent Color
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <input
                      type="color"
                      value={profile.secondary_color || '#f59e0b'}
                      onChange={(e) => handleChange('secondary_color', e.target.value)}
                      style={{ width: 44, height: 44, border: 'none', cursor: 'pointer', borderRadius: 4 }}
                    />
                    <TextField
                      size="small"
                      value={profile.secondary_color || '#f59e0b'}
                      onChange={(e) => handleChange('secondary_color', e.target.value)}
                      sx={{ flexGrow: 1 }}
                    />
                  </Box>
                  <Box sx={{ mt: 1.5, p: 1, bgcolor: profile.secondary_color || '#f59e0b', color: 'white', borderRadius: 1, textAlign: 'center', fontSize: '0.8rem', fontWeight: 600 }}>
                    Secondary Preview
                  </Box>
                </Paper>
              </Grid>

              <Grid item xs={12} md={4}>
                <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                    Success / Action Accent Color
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <input
                      type="color"
                      value={profile.accent_color || '#10b981'}
                      onChange={(e) => handleChange('accent_color', e.target.value)}
                      style={{ width: 44, height: 44, border: 'none', cursor: 'pointer', borderRadius: 4 }}
                    />
                    <TextField
                      size="small"
                      value={profile.accent_color || '#10b981'}
                      onChange={(e) => handleChange('accent_color', e.target.value)}
                      sx={{ flexGrow: 1 }}
                    />
                  </Box>
                  <Box sx={{ mt: 1.5, p: 1, bgcolor: profile.accent_color || '#10b981', color: 'white', borderRadius: 1, textAlign: 'center', fontSize: '0.8rem', fontWeight: 600 }}>
                    Accent Preview
                  </Box>
                </Paper>
              </Grid>
            </Grid>
          )}

          {/* TAB 5: Banking & UPI */}
          {activeTab === 5 && (
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                  Bank Account & UPI Payment Destination
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Bank details and UPI ID printed on PDF Invoices for customer NEFT/RTGS/IMPS/UPI payments.
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Bank Name"
                  value={profile.bank_name || ''}
                  onChange={(e) => handleChange('bank_name', e.target.value)}
                  placeholder="e.g. HDFC Bank"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Account Number"
                  value={profile.bank_account_number || ''}
                  onChange={(e) => handleChange('bank_account_number', e.target.value)}
                  placeholder="e.g. 50200012345678"
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="IFSC Code"
                  value={profile.bank_ifsc || ''}
                  onChange={(e) => handleChange('bank_ifsc', e.target.value.toUpperCase())}
                  placeholder="e.g. HDFC0001234"
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Branch Name"
                  value={profile.bank_branch || ''}
                  onChange={(e) => handleChange('bank_branch', e.target.value)}
                  placeholder="e.g. Daltonganj Branch"
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Business UPI ID"
                  value={profile.upi_id || ''}
                  onChange={(e) => handleChange('upi_id', e.target.value)}
                  placeholder="e.g. dhanshikaexports@hdfcbank"
                />
              </Grid>
            </Grid>
          )}

          {/* TAB 6: Document Notes & Terms */}
          {activeTab === 6 && (
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                  Default Invoice Notes, Footers & Terms
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Default headers, thank-you notes, and legal jurisdiction terms automatically populated on generated documents.
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Invoice / Estimate Header Note"
                  value={profile.invoice_header_note || ''}
                  onChange={(e) => handleChange('invoice_header_note', e.target.value)}
                  placeholder="e.g. Note: Subject to stock availability at dispatch time."
                  multiline
                  rows={2}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Invoice Footer Note / Thank You Note"
                  value={profile.invoice_footer_note || ''}
                  onChange={(e) => handleChange('invoice_footer_note', e.target.value)}
                  placeholder="e.g. Thank you for your business! Goods once sold are subject to company terms."
                  multiline
                  rows={2}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Standard Terms & Conditions"
                  value={profile.terms_and_conditions || ''}
                  onChange={(e) => handleChange('terms_and_conditions', e.target.value)}
                  placeholder="1. Payment terms as agreed..."
                  multiline
                  rows={4}
                />
              </Grid>
            </Grid>
          )}
        </CardContent>

        <Divider />
        <Box sx={{ p: 2, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={saving}
            startIcon={saving ? <CircularProgress size={18} color="inherit" /> : <SaveIcon />}
            sx={{
              bgcolor: '#1e3a8a',
              fontWeight: 600,
              '&:hover': { bgcolor: '#1e40af' },
            }}
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
        </Box>
      </Card>

      {/* Snackbar Alerts */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          sx={{ width: '100%', boxShadow: 3 }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  )
}

export default CompanyProfilePage
